import isEqual from 'lodash.isequal';
import moment from 'moment';

const toMomentDate = (date) => (date ? moment(date, 'YYYY-MM-DD') : null);
// A plain date, not a timestamp: a moment serialized as is would shift a day east of UTC
const toBackendDate = (date) => (date ? moment(date).format('YYYY-MM-DD') : null);

export const formatTreatmentProtocolResponse = (fetchedTreatmentProtocol) => ({
  id: fetchedTreatmentProtocol.id,
  active: fetchedTreatmentProtocol.active,
  name: fetchedTreatmentProtocol.name,
  comment: fetchedTreatmentProtocol.comment,
  farm_treatment_protocol: fetchedTreatmentProtocol.farm_treatment_protocol,
  applied_farms: fetchedTreatmentProtocol.farms?.filter((farm) => farm.active),
  tp_base_date: fetchedTreatmentProtocol.tp_base_date, // boolean
  applicable_phases: fetchedTreatmentProtocol.applicable_phases,
  effective_start_date: toMomentDate(fetchedTreatmentProtocol.effective_start_date),
  effective_end_date: toMomentDate(fetchedTreatmentProtocol.effective_end_date),
  treatments: fetchedTreatmentProtocol.treatment_protocol_products.map((product) => ({
    id: product.id,
    product: product.treatment_product,
    repeat: product.treatment_protocol_product_steps.some(({ repeat }) => repeat),
    comment: product.comment,
    dosage_comment: product.dosage_comment,
    steps: product.treatment_protocol_product_steps.map((step) => {
      const {
        step_type, step_unit, first_period, last_period, id, start_date, end_date, quantity, gallons_value,
        consecutive_duration
      } = step;

      return {
        [step_type]: {
          step_unit,
          first_period,
          last_period,
          start_date: start_date ? moment(start_date, 'YYYY-MM-DD') : null,
          end_date: end_date ? moment(end_date, 'YYYY-MM-DD') : null,
          quantity,
          gallons_value,
          consecutive_duration,
        },
        step_type,
        id
      };
    }),
  }))
});

export const formatTreatmentProtocolPayload = (treatmentProtocolPayload) => ({
  id: treatmentProtocolPayload.id,
  active: treatmentProtocolPayload.active,
  name: treatmentProtocolPayload.name,
  comment: treatmentProtocolPayload.comment,
  tp_base_date: treatmentProtocolPayload.tp_base_date, // boolean
  applicable_phases: treatmentProtocolPayload.applicable_phases,
  // undefined (not sent) from the forms that don't edit the effective dates, null clears one
  effective_start_date: treatmentProtocolPayload.effective_start_date === undefined
    ? undefined
    : toBackendDate(treatmentProtocolPayload.effective_start_date),
  effective_end_date: treatmentProtocolPayload.effective_end_date === undefined
    ? undefined
    : toBackendDate(treatmentProtocolPayload.effective_end_date),
  treatment_protocol_products_attributes: treatmentProtocolPayload.treatments.map((treatment) => ({
    id: treatment.id,
    treatment_product_id: treatment.product.id,
    repeat: treatment.repeat,
    comment: treatment.comment,
    dosage_comment: treatment.dosage_comment,
    _destroy: treatment._destroy,
    treatment_protocol_product_steps_attributes: treatment.steps.map((step) => {
      const { step_type, id, _destroy } = step;
      const {
        step_unit, first_period, last_period, start_date, end_date, quantity, gallons_value, consecutive_duration
      } = step[step_type];
      const repeat = step_type === 'between' && treatment.repeat;
      const isFlexible = step_type === 'flexible';

      return {
        step_unit,
        first_period,
        last_period,
        step_type,
        id,
        _destroy,
        repeat,
        start_date: step_type === 'before' ? undefined : start_date,
        // eslint-disable-next-line no-nested-ternary
        end_date: step_type === 'after' ? undefined : (step_type === 'on' ? start_date : end_date),
        quantity,
        gallons_value,
        consecutive_duration: isFlexible ? consecutive_duration : undefined,
      };
    }),
  }))
});

// TODO: replace it with adding _destroy key dynamically, on every remove call
export const markDestroyedItems = (updatedTreatmentProtocol, initialTreatmentProtocol) => {
  const formattedTreatmentProtocol = { ...updatedTreatmentProtocol };
  initialTreatmentProtocol.treatments.forEach((treatment) => {
    const treatmentItem = formattedTreatmentProtocol.treatments.find(({ id }) => id === treatment.id);
    if (treatmentItem) {
      treatment.steps.forEach((step) => {
        const stepItem = treatmentItem.steps.find(({ id }) => id === step.id);
        if (!stepItem) {
          treatmentItem.steps.push({ ...step, _destroy: true });
        }
      });
    } else {
      formattedTreatmentProtocol.treatments.push({ ...treatment, _destroy: true });
    }
  });

  return formattedTreatmentProtocol;
};

const isTreatmentStepFilled = (treatmentStepData = {}, product = {}) => {
  const { step_type } = treatmentStepData;
  const {
    step_unit, first_period, last_period, start_date, end_date, quantity, gallons_value, consecutive_duration
  } = treatmentStepData[step_type] || {};

  if (!product.route.includes('topical') && !parseFloat(quantity)) return false;
  if (
    product.dosage_measure === 'gal' && !product.route.includes('topical') &&
    !parseFloat(gallons_value)
  ) return false;

  if (step_type === 'flexible') {
    const windowFilled = step_unit === 'date_range'
      ? (start_date && end_date)
      : (first_period && last_period && step_unit);
    return !!(windowFilled && consecutive_duration);
  }
  if (step_unit === 'date_range' && step_type === 'between') return start_date && end_date;
  if (step_unit === 'date_range' && ['after', 'on', 'before'].includes(step_type)) return start_date || end_date;
  if (step_type === 'between') return first_period && last_period && step_unit;

  return step_type && first_period && step_unit;
};

export const validateTreatmentProtocol = (treatmentProtocol = {}) => {
  const isProtocolNamed = !!treatmentProtocol.name;
  if (!isProtocolNamed) return false;
  return treatmentProtocol.treatments?.every((treatment) => {
    const isProductSelected = !!treatment.product;
    if (!isProductSelected) return false;
    return treatment.steps?.every((step) => isTreatmentStepFilled(step, treatment.product));
  });
};

// A blank bound is open; set on both sides, the range cannot end before it starts
export const isEffectiveDatesRangeValid = ({ effective_start_date, effective_end_date } = {}) => {
  if (!effective_start_date || !effective_end_date) return true;
  return !moment(effective_end_date).isBefore(moment(effective_start_date), 'day');
};

export const validateUpdatedTreatmentProtocol = (updatedTreatmentProtocol, initialTreatmentProtocol) => {
  return !isEqual(updatedTreatmentProtocol, initialTreatmentProtocol) &&
    validateTreatmentProtocol(updatedTreatmentProtocol);
};
